import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { realtimeService, type NotificationMessage } from '@/services/realtimeService';

interface NotificationContextType {
  notifications: NotificationMessage[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  addNotification: (notification: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => void;
  showNotification: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

interface SnackbarState {
  open: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    type: 'info',
  });

  useEffect(() => {
    // Subscribe to real-time notifications
    const unsubscribe = realtimeService.subscribeToNotifications((notification) => {
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === notification.id)) {
          return prev;
        }
        return [notification, ...prev].slice(0, 50); // Keep only last 50
      });
      
      updateUnreadCount();
    });

    // Initial load
    setNotifications(realtimeService.getNotifications());
    updateUnreadCount();

    return unsubscribe;
  }, []);

  const updateUnreadCount = () => {
    setUnreadCount(realtimeService.getUnreadCount());
  };

  const markAsRead = (id: string) => {
    realtimeService.markNotificationAsRead(id);
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    updateUnreadCount();
  };

  const markAllAsRead = () => {
    notifications.forEach(notification => {
      if (!notification.read) {
        realtimeService.markNotificationAsRead(notification.id);
      }
    });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const addNotification = (notificationData: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => {
    const notification: NotificationMessage = {
      ...notificationData,
      id: `manual-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      read: false,
    };

    setNotifications(prev => [notification, ...prev].slice(0, 50));
    updateUnreadCount();
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    addNotification({
      type: type,
      title: type.charAt(0).toUpperCase() + type.slice(1),
      message: message
    });
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    addNotification,
    showNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}